/**
 * Multi-Tenant Provider Portal V2
 * Self-service portal for external providers (MDs and Community Consultants)
 * Features: Switch Clinic, multi-hospital referrals, analytics
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Phone, Building2, ArrowRight, LogOut, Plus, FileText, 
  BarChart3, Clock, CheckCircle, AlertCircle, Users,
  QrCode, Send, Search, Filter, ChevronRight, RefreshCw,
  Stethoscope, Heart, MapPin, Calendar, TrendingUp, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== LOGIN COMPONENT ====================
const ProviderLogin = ({ onLogin }) => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone', 'otp', or 'activating'
  const [loading, setLoading] = useState(false);

  // Handle activation token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setStep('activating');
      activateAccount(token);
    }
  }, []);

  const activateAccount = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/provider-management/providers/activate?token=${token}`, {
        method: 'POST'
      });
      
      // Read the response body only once
      let data;
      let errorText = '';
      
      try {
        const text = await response.text();
        errorText = text;
        data = JSON.parse(text);
      } catch (parseError) {
        // If JSON parsing fails, use the text as error
        throw new Error(errorText || 'Activation failed');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Activation failed');
      }
      
      toast.success(data.message || 'Account activated successfully! You can now log in.');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      setStep('phone');
    } catch (error) {
      toast.error(error.message || 'Activation failed. Please contact reception.');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      setStep('phone');
    }
  };

  const requestOTP = async () => {
    if (!phone.trim() || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/provider-portal-v2/request-otp?phone=${phone.trim()}`, {
        method: 'POST'
      });
      
      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, try to get text
        const text = await responseClone.text();
        throw new Error(text || 'Failed to send OTP');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to send OTP');
      }
      
      toast.success('OTP sent successfully');
      if (data.dev_otp) {
        toast.info(`Dev OTP: ${data.dev_otp}`, { duration: 10000 });
      }
      setStep('otp');
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/provider-portal-v2/verify-otp?phone=${phone.trim()}&otp=${otp.trim()}`,
        { method: 'POST' }
      );
      
      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        const text = await responseClone.text();
        throw new Error(text || 'Invalid OTP');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Invalid OTP');
      }
      
      onLogin(data);
    } catch (error) {
      toast.error(error.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
              <Stethoscope className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Provider Portal</CardTitle>
            <CardDescription className="text-gray-600">
              {step === 'activating' 
                ? 'Activating your account...' 
                : step === 'phone' 
                  ? 'Enter your registered phone number' 
                  : 'Enter the OTP sent to your phone'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 'activating' ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
                <p className="text-gray-600">Please wait while we activate your account...</p>
              </div>
            ) : step === 'phone' ? (
              <>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="tel"
                    placeholder="Phone number (e.g., 9876543210)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="pl-10 h-12 text-lg"
                    data-testid="provider-phone-input"
                  />
                </div>
                <Button
                  onClick={requestOTP}
                  disabled={loading || phone.length < 10}
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  data-testid="provider-request-otp-btn"
                >
                  {loading ? 'Sending...' : 'Get OTP'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </>
            ) : (
              <>
                <div className="text-center text-sm text-gray-600 mb-2">
                  OTP sent to <span className="font-semibold">+91 {phone}</span>
                </div>
                <Input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="h-12 text-center text-2xl tracking-widest"
                  maxLength={6}
                  data-testid="provider-otp-input"
                />
                <Button
                  onClick={verifyOTP}
                  disabled={loading || otp.length !== 6}
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  data-testid="provider-verify-otp-btn"
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setStep('phone'); setOtp(''); }}
                  className="w-full"
                >
                  Change Phone Number
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// ==================== HOSPITAL SELECTOR ====================
const HospitalSelector = ({ hospitals, provider, onSelect, onLogout }) => {
  const [loading, setLoading] = useState(null);

  const selectHospital = async (hospital) => {
    setLoading(hospital.hospital_id);
    try {
      const token = localStorage.getItem('provider_token');
      const response = await fetch(
        `${API_URL}/api/provider-portal-v2/select-hospital?hospital_id=${hospital.hospital_id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const responseClone = response.clone();
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        const text = await responseClone.text();
        throw new Error(text || 'Failed to select hospital');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to select hospital');
      }
      
      onSelect(data, hospital);
    } catch (error) {
      toast.error(error.message || 'Failed to select hospital');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Welcome, {provider.full_name}
            </CardTitle>
            <CardDescription className="text-gray-600">
              Which hospital would you like to work with?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {hospitals.map((hospital) => (
              <motion.div
                key={hospital.hospital_id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <button
                  onClick={() => selectHospital(hospital)}
                  disabled={loading === hospital.hospital_id}
                  className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left flex items-center justify-between group"
                  data-testid={`select-hospital-${hospital.hospital_id}`}
                  style={hospital.branding?.primary_color ? {
                    '--hover-color': hospital.branding.primary_color
                  } : {}}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: hospital.branding?.primary_color || '#10B981' }}
                    >
                      {hospital.hospital_name?.charAt(0) || 'H'}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{hospital.hospital_name}</div>
                      <div className="text-sm text-gray-500">
                        Code: {hospital.provider_code} • {hospital.referral_count || 0} referrals
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" />
                </button>
              </motion.div>
            ))}
            
            <div className="pt-4 border-t">
              <Button
                variant="ghost"
                onClick={onLogout}
                className="w-full text-gray-500 hover:text-red-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// ==================== REFERRAL FORM ====================
const ReferralForm = ({ session, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_mobile: '',
    patient_age: '',
    patient_gender: '',
    patient_email: '',
    patient_address: '',
    village: '',
    priority: 'routine',
    chief_complaint: '',
    reason_text: '',
    input_language: 'english'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.patient_name || !formData.patient_mobile) {
      toast.error('Patient name and mobile are required');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('provider_session_token');
      const response = await fetch(`${API_URL}/api/provider-portal-v2/referrals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          target_hospital_id: session.active_hospital_id,
          patient_age: formData.patient_age ? parseInt(formData.patient_age) : null
        })
      });
      
      const responseClone = response.clone();
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        const text = await responseClone.text();
        throw new Error(text || 'Failed to create referral');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to create referral');
      }
      
      toast.success(`Referral ${data.referral.ref_id} created successfully!`);
      onSuccess(data);
    } catch (error) {
      toast.error(error.message || 'Failed to create referral');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">New Patient Referral</h2>
          <p className="text-sm text-gray-500">Referring to {session.active_hospital_name}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name *</label>
              <Input
                value={formData.patient_name}
                onChange={(e) => setFormData({...formData, patient_name: e.target.value})}
                placeholder="Full name"
                required
                data-testid="referral-patient-name"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
              <Input
                type="tel"
                value={formData.patient_mobile}
                onChange={(e) => setFormData({...formData, patient_mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                placeholder="10-digit mobile"
                required
                data-testid="referral-patient-mobile"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
              <Input
                type="number"
                value={formData.patient_age}
                onChange={(e) => setFormData({...formData, patient_age: e.target.value})}
                placeholder="Age"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <Select value={formData.patient_gender} onValueChange={(v) => setFormData({...formData, patient_gender: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Village/Area</label>
              <Input
                value={formData.village}
                onChange={(e) => setFormData({...formData, village: e.target.value})}
                placeholder="Village or area"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                type="email"
                value={formData.patient_email}
                onChange={(e) => setFormData({...formData, patient_email: e.target.value})}
                placeholder="Email (optional)"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <Input
              value={formData.patient_address}
              onChange={(e) => setFormData({...formData, patient_address: e.target.value})}
              placeholder="Full address"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chief Complaint</label>
            <Input
              value={formData.chief_complaint}
              onChange={(e) => setFormData({...formData, chief_complaint: e.target.value})}
              placeholder="Primary reason for referral"
              data-testid="referral-chief-complaint"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Reason for Referral</label>
              <Select value={formData.input_language} onValueChange={(v) => setFormData({...formData, input_language: v})}>
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">English</SelectItem>
                  <SelectItem value="hindi">Hindi</SelectItem>
                  <SelectItem value="kannada">Kannada</SelectItem>
                  <SelectItem value="marathi">Marathi</SelectItem>
                  <SelectItem value="tamil">Tamil</SelectItem>
                  <SelectItem value="telugu">Telugu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={formData.reason_text}
              onChange={(e) => setFormData({...formData, reason_text: e.target.value})}
              placeholder="Detailed reason for referral (will be translated to clinical English if needed)"
              rows={3}
            />
          </div>
          
          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
              data-testid="submit-referral-btn"
            >
              {loading ? 'Creating...' : 'Create Referral'}
              <Send className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ==================== REFERRAL QR MODAL ====================
const ReferralQRModal = ({ referral, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-2xl p-6 max-w-sm w-full text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900">{referral.ref_id}</h3>
          <p className="text-sm text-gray-500">{referral.patient_name}</p>
        </div>
        
        {referral.qr_code_data && (
          <div className="bg-white p-4 rounded-xl border inline-block mb-4">
            <img 
              src={`data:image/png;base64,${referral.qr_code_data}`}
              alt="QR Code"
              className="w-48 h-48"
            />
          </div>
        )}
        
        <p className="text-sm text-gray-600 mb-4">
          Show this QR code at the hospital reception
        </p>
        
        <Button onClick={onClose} className="w-full">
          Close
        </Button>
      </motion.div>
    </motion.div>
  );
};

// ==================== PROVIDER DASHBOARD ====================
const ProviderDashboard = ({ session, branding, onSwitchHospital, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('provider_session_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, referralsRes] = await Promise.all([
        fetch(`${API_URL}/api/provider-portal-v2/dashboard/stats`, { headers }),
        fetch(`${API_URL}/api/provider-portal-v2/referrals?limit=50`, { headers })
      ]);
      
      const statsData = await statsRes.json();
      const referralsData = await referralsRes.json();
      
      setStats(statsData);
      setReferrals(referralsData.referrals || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const primaryColor = branding?.primary_color || '#10B981';

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      received: { color: 'bg-blue-100 text-blue-800', label: 'Received' },
      patient_arrived: { color: 'bg-purple-100 text-purple-800', label: 'Patient Arrived' },
      admitted: { color: 'bg-green-100 text-green-800', label: 'Admitted' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const filteredReferrals = filter === 'all' 
    ? referrals 
    : referrals.filter(r => r.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header 
        className="bg-gradient-to-r text-white shadow-lg"
        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Stethoscope className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{session.active_hospital_name}</h1>
                <p className="text-sm opacity-80">Provider: {session.provider_name} ({session.provider_code})</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={onSwitchHospital}
                className="text-white hover:bg-white/20"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Switch Clinic
              </Button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Referrals</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total_referrals || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.pending_referrals || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Patients Arrived</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.patients_arrived || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Today</p>
                  <p className="text-2xl font-bold text-blue-600">{stats?.today_referrals || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions & Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowReferralForm(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
              data-testid="new-referral-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Referral
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="patient_arrived">Arrived</SelectItem>
                <SelectItem value="admitted">Admitted</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Referrals List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">My Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredReferrals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No referrals found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredReferrals.map((referral) => (
                  <motion.div
                    key={referral.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedReferral(referral)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{referral.patient_name}</span>
                            <Badge variant="outline" className="text-xs">{referral.ref_id}</Badge>
                          </div>
                          <div className="text-sm text-gray-500">
                            {referral.chief_complaint || 'No complaint specified'} • {referral.patient_mobile}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {referral.priority === 'urgent' && (
                          <Badge className="bg-orange-100 text-orange-800">Urgent</Badge>
                        )}
                        {referral.priority === 'emergency' && (
                          <Badge className="bg-red-100 text-red-800">Emergency</Badge>
                        )}
                        {getStatusBadge(referral.status)}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReferral(referral);
                          }}
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {showReferralForm && (
          <ReferralForm
            session={session}
            onSuccess={(data) => {
              setShowReferralForm(false);
              setSelectedReferral(data.referral);
              fetchData();
            }}
            onCancel={() => setShowReferralForm(false)}
          />
        )}
        {selectedReferral && (
          <ReferralQRModal
            referral={selectedReferral}
            onClose={() => setSelectedReferral(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const ProviderPortalV2 = () => {
  const [state, setState] = useState({
    phase: 'loading',
    provider: null,
    hospitals: [],
    session: null,
    branding: null,
    providerToken: null
  });

  useEffect(() => {
    // Check for existing session
    const sessionToken = localStorage.getItem('provider_session_token');
    const sessionData = localStorage.getItem('provider_session_data');
    const branding = localStorage.getItem('provider_hospital_branding');
    
    if (sessionToken && sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        const parsedBranding = branding ? JSON.parse(branding) : null;
        // Use setTimeout to avoid setState in effect body
        setTimeout(() => {
          setState({
            phase: 'dashboard',
            session: parsed,
            branding: parsedBranding,
            provider: null,
            hospitals: [],
            providerToken: null
          });
        }, 0);
        return;
      } catch (e) {
        // Invalid session, clear and start fresh
        localStorage.removeItem('provider_session_token');
        localStorage.removeItem('provider_session_data');
      }
    }
    
    setTimeout(() => setState(prev => ({ ...prev, phase: 'login' })), 0);
  }, []);

  const handleLogin = async (data) => {
    localStorage.setItem('provider_token', data.access_token);
    localStorage.setItem('provider_data', JSON.stringify(data.provider));
    localStorage.setItem('provider_hospitals', JSON.stringify(data.hospitals));
    
    if (data.hospitals.length === 1) {
      // Auto-select single hospital - need to call select-hospital API first
      try {
        const response = await fetch(
          `${API_URL}/api/provider-portal-v2/select-hospital?hospital_id=${data.hospitals[0].hospital_id}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${data.access_token}` }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to select hospital');
        }
        
        const sessionData = await response.json();
        handleHospitalSelect(sessionData, data.hospitals[0]);
      } catch (error) {
        // If auto-select fails, show hospital selection screen
        toast.error('Auto-selection failed, please select manually');
        setState({
          phase: 'select_hospital',
          provider: data.provider,
          hospitals: data.hospitals,
          session: null,
          branding: null,
          providerToken: data.access_token
        });
      }
    } else {
      setState({
        phase: 'select_hospital',
        provider: data.provider,
        hospitals: data.hospitals,
        session: null,
        branding: null,
        providerToken: data.access_token
      });
    }
  };

  const handleHospitalSelect = async (sessionData, hospital) => {
    localStorage.setItem('provider_session_token', sessionData.session_token);
    localStorage.setItem('provider_session_data', JSON.stringify(sessionData.session));
    localStorage.setItem('provider_hospital_branding', JSON.stringify(hospital.branding || sessionData.hospital_branding));
    
    setState({
      phase: 'dashboard',
      session: sessionData.session,
      branding: hospital.branding || sessionData.hospital_branding,
      provider: null,
      hospitals: [],
      providerToken: null
    });
  };

  const handleSwitchHospital = async () => {
    localStorage.removeItem('provider_session_token');
    localStorage.removeItem('provider_session_data');
    
    const providerToken = localStorage.getItem('provider_token');
    const hospitals = JSON.parse(localStorage.getItem('provider_hospitals') || '[]');
    const provider = JSON.parse(localStorage.getItem('provider_data') || '{}');
    
    if (hospitals.length > 1) {
      setState({
        phase: 'select_hospital',
        provider,
        hospitals,
        session: null,
        branding: null,
        providerToken
      });
    } else {
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('provider_token');
    localStorage.removeItem('provider_data');
    localStorage.removeItem('provider_hospitals');
    localStorage.removeItem('provider_session_token');
    localStorage.removeItem('provider_session_data');
    localStorage.removeItem('provider_hospital_branding');
    
    setState({
      phase: 'login',
      provider: null,
      hospitals: [],
      session: null,
      branding: null,
      providerToken: null
    });
  };

  if (state.phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (state.phase === 'login') {
    return <ProviderLogin onLogin={handleLogin} />;
  }

  if (state.phase === 'select_hospital') {
    return (
      <HospitalSelector
        hospitals={state.hospitals}
        provider={state.provider}
        onSelect={handleHospitalSelect}
        onLogout={handleLogout}
      />
    );
  }

  if (state.phase === 'dashboard') {
    return (
      <ProviderDashboard
        session={state.session}
        branding={state.branding}
        onSwitchHospital={handleSwitchHospital}
        onLogout={handleLogout}
      />
    );
  }

  return null;
};

export default ProviderPortalV2;
